"use client";

import React from "react";
import { Activity, TrendingUp, TrendingDown, Minus, AlertTriangle } from "lucide-react";
import { CompanyInfoCard } from "./CompanyInfoCard";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCompanyHooks } from "@/lib/hooks/api/shared/companies-graphql";
import { toast } from "sonner";
import type { CompanyEntity } from "@/lib/types/company";
import type { Project_Health_Status } from "@/src/gql/graphql";

interface CompanyProjectHealthProps {
  company: CompanyEntity;
  onRefresh?: () => void;
}

const STATUS_CONFIG: Record<Project_Health_Status, { label: string; variant: "default" | "destructive" | "secondary" | "outline"; icon: React.ReactNode }> = {
  green: {
    label: "On Track",
    variant: "default",
    icon: <TrendingUp className="h-3 w-3" />
  },
  yellow: {
    label: "At Risk",
    variant: "secondary",
    icon: <Minus className="h-3 w-3" />
  },
  red: {
    label: "Off Track",
    variant: "destructive",
    icon: <TrendingDown className="h-3 w-3" />
  },
};

const STATUS_ORDER: Project_Health_Status[] = ["green", "yellow", "red"];

// Sentinel for the "clear" option — Radix Select items cannot use an empty
// string value, so we map this back to null on save.
const NONE_VALUE = "__none__";

const HEALTH_FIELDS = [
  { key: "scope_status", label: "Scope" },
  { key: "schedule_status", label: "Schedule" },
  { key: "budget_status", label: "Budget" },
  { key: "alignment_status", label: "Alignment" },
] as const satisfies readonly { key: keyof CompanyEntity; label: string }[];

type HealthFieldKey = (typeof HEALTH_FIELDS)[number]["key"];

function isHealthStatus(value: unknown): value is Project_Health_Status {
  return value === "green" || value === "yellow" || value === "red";
}

/**
 * A single inline-editable project-health indicator. Renders a read-only badge
 * showing the current status (or "Not Set"); selecting a status from the inline
 * Select persists it immediately via useCompanyHooks().useUpdate(). A "Not set"
 * option clears the value back to null. Mirrors EditableCompanyDescription:
 * a local mirror seeded from the prop, re-seeded when the company changes, with
 * onRefresh() called after a successful save for immediate display.
 */
function EditableHealthStatus({
  company,
  field,
  label,
  onRefresh,
}: {
  company: CompanyEntity;
  field: HealthFieldKey;
  label: string;
  onRefresh?: () => void;
}) {
  const { useUpdate } = useCompanyHooks();
  const { mutateAsync, isPending } = useUpdate();

  const propValue: Project_Health_Status | null = isHealthStatus(company[field])
    ? company[field]
    : null;

  // Local mirror so the saved value shows immediately even without a refetch.
  const [value, setValue] = React.useState<Project_Health_Status | null>(propValue);

  // Re-seed when the underlying company / value changes.
  React.useEffect(() => {
    setValue(propValue);
  }, [propValue, company.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const config = value ? STATUS_CONFIG[value] : null;

  const save = async (next: Project_Health_Status | null) => {
    if (next === value) return;
    const previous = value;
    setValue(next); // optimistic
    try {
      await mutateAsync({ id: company.id, [field]: next });
      toast.success(
        next
          ? `${label} set to ${STATUS_CONFIG[next].label}`
          : `${label} status cleared`
      );
      onRefresh?.();
    } catch (err) {
      setValue(previous); // revert on failure
      toast.error(
        err instanceof Error ? err.message : `Failed to update ${label.toLowerCase()} status`
      );
    }
  };

  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
        {label}
      </span>
      <Select
        value={value ?? NONE_VALUE}
        disabled={isPending}
        onValueChange={(v) => void save(v === NONE_VALUE ? null : (v as Project_Health_Status))}
      >
        <SelectTrigger
          className="h-auto w-auto gap-1 border-none bg-transparent p-0 shadow-none focus:ring-0 [&>svg]:opacity-50 hover:[&>svg]:opacity-100"
          aria-label={`${label} status`}
        >
          <SelectValue>
            {config ? (
              <Badge variant={config.variant} className="flex items-center gap-1">
                {config.icon}
                {config.label}
              </Badge>
            ) : (
              <Badge variant="outline">Set status</Badge>
            )}
          </SelectValue>
        </SelectTrigger>
        <SelectContent align="end">
          {STATUS_ORDER.map((status) => (
            <SelectItem key={status} value={status}>
              <span className="flex items-center gap-2">
                {STATUS_CONFIG[status].icon}
                {STATUS_CONFIG[status].label}
              </span>
            </SelectItem>
          ))}
          <SelectItem value={NONE_VALUE}>
            <span className="text-gray-500">Not set</span>
          </SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}

export function CompanyProjectHealth({ company, onRefresh }: CompanyProjectHealthProps) {
  const anyAtRisk = HEALTH_FIELDS.some(({ key }) => {
    const status = company[key];
    return status === "yellow" || status === "red";
  });

  return (
    <CompanyInfoCard
      title="Project Health"
      icon={Activity}
      className={anyAtRisk ? "border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/40" : ""}
    >
      {anyAtRisk && (
        <div className="flex items-start gap-2 p-3 bg-amber-100 border border-amber-200 rounded-md mb-3 dark:bg-amber-900/40 dark:border-amber-800">
          <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-amber-800 dark:text-amber-200">
            This company has one or more project health indicators at risk or off track.
          </p>
        </div>
      )}

      <div className="space-y-3">
        {HEALTH_FIELDS.map(({ key, label }) => (
          <EditableHealthStatus
            key={key}
            company={company}
            field={key}
            label={label}
            onRefresh={onRefresh}
          />
        ))}
      </div>
    </CompanyInfoCard>
  );
}
